import { generateNodeId } from "@/components/conversation-renderer/conversation-renderer-util";
import { IntentLocale } from "@/locales/localeid";
import { ConversationBuilderState } from "@/modules/conversation-builder";
import { Paths } from "@/util/paths";
import { Button, Divider, Drawer, Form, Input, Modal, Select } from "ant-design-vue";
import { WrappedFormUtils } from "ant-design-vue/types/form/form";
import { debounce, includes, pull } from "lodash";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import { entityNameLengthValidator, entityNameValidator, manageEntityNameUniqueValidator } from ".";
import { NormalizeSpaces } from "../../conversation-builder-util";
import {
	ConversationNode,
	ConversationNodeType,
	EntityDataSet,
	EntityDataSetDirectory,
	Path
} from "../../conversation-builder.model";
import { CommonEntity, Entity } from "../../models/entity.model";
import { Intent } from "../../models/intent.model";
import { EntityValues } from "../entity-values";
import { intentNameValidator, intentManageValidator, intentNameLengthValidator } from "../intent-add-dialog";
import { IntentUtterance } from "../intent-utterance";

@Component({
	components: {
		Drawer,
		Form,
		FormItem: Form.Item,
		Input,
		Divider,
		Select,
		SelectOption: Select.Option,
		Button,
		IntentUtterance,
		EntityValues
	}
})
export default class IntentManageDrawer extends Vue {
	@Prop() intent!: Intent;
	@Prop() node!: ConversationNode<Path>;

	public IntentLocale = IntentLocale;

	public intentNameValidator?: Function;
	public intentNameLengthValidator?: Function;
	public intentUniqueValidator?: Function;
	public entityNameUniqueValidator?: Function;
	public entityNameLengthValidator?: Function;
	public entityNameValidator?: Function;

	public wrapStyle: { [key: string]: string | null } = {};
	public maskStyle: { [key: string]: string | null } = {};
	public form!: WrappedFormUtils;

	private conversationStore = getModule(ConversationBuilderState);
	public usage: number | null = null;

	public selectedEntity: Entity | null = null;
	private entitySearchKeyword: string = "";

	$refs!: {
		addUtterence: HTMLInputElement;
		editedUtteranceInput: Vue;
		utterancesInput: HTMLDivElement;
		nameInput: HTMLInputElement;
		entitySelect: HTMLSelectElement;
		entityInput: HTMLInputElement;
	};

	created() {
		setTimeout(() => {
			this.$refs.nameInput.focus();
		}, 100);
		this.setDrawerPosition();
		this.onIntentSelect();
	}

	@Watch("intent")
	onIntentSelect() {
		this.init();
		// Initial validation does not work without timeout.
		setTimeout(() => {
			this.form.validateFields();
		}, 100);
	}

	@Watch("conversationStore.nodes", { deep: true })
	setIntentUsage() {
		if (this.intent) {
			let usage = 0;
			Object.keys(this.conversationStore.nodes).forEach((nodeId) => {
				const node = this.conversationStore.nodes[nodeId];
				if (node.type === ConversationNodeType.Path) {
					const data = node.data as Path;
					const isUsed = data.intents.find((intent) => intent.id === this.intent.id);
					if (isUsed) {
						usage++;
					}
				}
			});
			this.usage = usage;
		} else {
			this.usage = null;
		}
	}

	public get isCommonIntent(): boolean {
		return !!this.intent.commonIntentId;
	}
	public get isCommonEntity(): boolean {
		return !!this.selectedEntity?.commonEntityId;
	}

	public get commonEntites(): EntityDataSet<CommonEntity> {
		return this.conversationStore.commonEntites;
	}

	public get appEntites(): EntityDataSetDirectory<Entity> {
		return this.conversationStore.entities.directory;
	}

	public init() {
		this.intentNameValidator = intentNameValidator;
		this.intentNameLengthValidator = intentNameLengthValidator;
		this.intentUniqueValidator = intentManageValidator(this.intent.id);
		this.setSelectedEntity();
		this.setIntentUsage();
		this.form = this.$form.createForm(this, {
			name: "intent-manage",
			onValuesChange: debounce(this.onValuesChange, 500)
		});
	}

	normalize(value) {
		return NormalizeSpaces(value);
	}

	public onValuesChange(props: any, fields: Partial<IntentFormFields>) {
		if (fields.name || fields.entityId) {
			const intentName = fields.name || props.form.getFieldValue("name");
			const intentStoreValue = this.conversationStore.intents.directory[this.intent.id];
			const intent = {
				...intentStoreValue,
				...fields,
				name: NormalizeSpaces(intentName)
			};
			this.conversationStore.updateIntent({ id: this.intent.id, intent });
			this.$emit("updateIntent", intent);
		}
		if (fields.entityName && this.selectedEntity && !this.isCommonEntity) {
			this.conversationStore.updateEntityName({
				id: this.selectedEntity.id,
				name: NormalizeSpaces(fields.entityName)
			});
			this.$emit("updateIntent", this.conversationStore.intents.directory[this.intent.id]);
		}
	}

	public setDrawerPosition() {
		const rect = document.getElementsByClassName("cb__right")?.[0].getBoundingClientRect();
		if (rect) {
			this.wrapStyle = {
				top: rect.top + "px",
				right: window.innerWidth - rect.left + 5 + "px",
				height: rect.height + "px"
			};
			this.maskStyle = {
				top: `-${rect.top}px`,
				height: window.innerHeight + "px"
			};
		} else {
			this.wrapStyle = {};
			this.maskStyle = {};
		}
	}

	public setSelectedEntity(entityIdFromSelect?: string) {
		const entityId = entityIdFromSelect || this.intent.entityId;
		if (entityId && this.conversationStore.entities.directory[entityId]) {
			this.selectedEntity = this.conversationStore.entities.directory[entityId];
			this.entityNameUniqueValidator = manageEntityNameUniqueValidator(this.selectedEntity.id);
			this.entityNameValidator = entityNameValidator;
			this.entityNameLengthValidator = entityNameLengthValidator;
		} else {
			this.selectedEntity = null;
		}
	}

	public onEntityChange(entityIdFromSelect?: string) {
		const continueWithChange = () => {
			// If previous entity hasn't been used in any other intent so we're deleting that entity.
			if (this.selectedEntity) {
				if (!entityIdFromSelect) {
					this.conversationStore.updateIntent({ id: this.intent.id, intent: { entityId: undefined } });
				}
				this.conversationStore.removeEntityIfNoReferenceExists(this.selectedEntity.id);
			}

			// Proceeding with the entity change
			if (entityIdFromSelect) {
				const commonEntityId = includes(this.commonEntites.ids, entityIdFromSelect);
				// Adding common entity to local conversation meta data
				if (commonEntityId) {
					const commonEntity = this.commonEntites.directory[entityIdFromSelect];
					this.conversationStore.addEntity({
						...commonEntity,
						commonEntityId: commonEntity.id,
						key: 0,
						order: 0
					});
				}
				this.setSelectedEntity(entityIdFromSelect);
			} else {
				this.selectedEntity = null;
			}
		};

		if (this.selectedEntity) {
			// Create closure of this.selectedEntity in selectedEntity variable because it is not available on modal events.
			const selectedEntity = this.selectedEntity;
			Modal.confirm({
				title: this.$format(IntentLocale.IntentEntityChangeConfirmationTitle),
				content: this.$format(IntentLocale.IntentEntityChangeConfirmationText, {
					entityName: this.selectedEntity.name
				}),
				onOk: () => {
					this.removedUtterances(selectedEntity!.name);

					// Paths that use the same intent.
					const paths: ConversationNode<Path>[] = Object.keys(this.conversationStore.nodes)
						.filter(
							(key) =>
								this.conversationStore.nodes[key] &&
								this.conversationStore.nodes[key].type === ConversationNodeType.Path &&
								(this.conversationStore.nodes[key].data as Path).intents.some(
									(i) => i.id === this.intent.id
								)
						)
						.map((key) => this.conversationStore.nodes[key] as ConversationNode<Path>);

					// Paths that use the same intent but there should not be any other intent in the path which uses the same entity.
					// Otherwise it will remove entity values for the path, even if there is another intent which uses the same entity.
					const updatePaths = paths.filter(
						(p) =>
							!p.data.intents.some(
								(i) =>
									i.id !== this.intent.id &&
									this.conversationStore.intents.directory[i.id] &&
									this.conversationStore.intents.directory[i.id].entityId &&
									this.conversationStore.intents.directory[i.id].entityId === selectedEntity!.id
							)
					);

					// Remove entity value references from above paths as well.
					updatePaths.forEach((p) => {
						this.conversationStore.removeEntityValueReferencesFromPath({
							entityId: selectedEntity!.id,
							nodeId: p.id
						});
					});

					continueWithChange();
					this.$nextTick(() => {
						this.$refs.entitySelect?.focus();
						this.$refs.entityInput?.focus();
					});
				},
				onCancel: () => {
					this.form.setFieldsValue({ entityId: selectedEntity!.id });
					this.$refs.entityInput?.focus();
					this.$refs.entitySelect?.focus();
				}
			});
		} else {
			continueWithChange();
		}
	}

	private removedUtterances(entityName: string) {
		const intent = this.conversationStore.intents.directory[this.intent.id];
		const utterances = [
			...intent.utterances.filter((utterance) => {
				return utterance.value.indexOf(`{${entityName}}`) === -1;
			})
		];

		const updateIntent = {
			...intent,
			utterances
		};

		this.conversationStore.updateIntent({
			id: intent.id,
			intent: updateIntent
		});

		this.$emit("updateIntent", updateIntent);
	}

	public entityfilterOption(keyword: string, option) {
		this.entitySearchKeyword = keyword;
		return option.componentOptions.children[0].text.toLowerCase().indexOf(keyword.toLowerCase()) >= 0;
	}

	public createNewEntity() {
		const entity: Entity = {
			id: generateNodeId(),
			name: this.entitySearchKeyword,
			key: 0,
			order: 0,
			values: []
		};
		this.conversationStore.addEntity(entity);
		this.conversationStore.updateIntent({ id: this.intent.id, intent: { entityId: entity.id } });
		this.onEntityChange(entity.id);
	}

	public removeEntity() {
		this.onEntityChange(undefined);
	}

	public close() {
		this.$emit("close");
	}

	onDisconnectIntent() {
		const intent = this.conversationStore.intents.directory[this.intent.id];

		if (intent) {
			const newId = generateNodeId();

			const newIntent: Intent = {
				...intent,
				id: newId,
				commonIntentId: "",
				utterances: intent.utterances.map((u) => ({
					...u,
					id: generateNodeId()
				}))
			};

			this.$emit("onIntentRemove", intent, true, true);
			this.conversationStore.addIntent(newIntent);

			if (this.node && this.node.id) {
				this.conversationStore.updateNodeData({
					id: this.node.id,
					data: {
						...this.node.data,
						intents: [...this.node.data.intents.filter((i) => i.id !== intent.id), newIntent]
					}
				});
			}

			this.$emit("updateIntent", newIntent);
			// Validation does not work without next tick.
			Vue.nextTick(() => {
				this.form.validateFields();
			});
		}
	}

	onDisconnectEntity() {
		if (!this.selectedEntity) {
			return;
		}

		// generate new id and old - new id map.
		const newId = generateNodeId();
		const newOldValueIdMap = this.selectedEntity.values.reduce((acc, item) => {
			acc[item.id] = generateNodeId();
			return acc;
		}, {});

		// create new entity.
		const newEntity: Entity = {
			...this.selectedEntity,
			id: newId,
			commonEntityId: "",
			values: this.selectedEntity.values.map((v) => ({
				...v,
				id: newOldValueIdMap[v.id]
			}))
		};

		// Paths, that use the same intent.
		const paths: ConversationNode<Path>[] = Object.keys(this.conversationStore.nodes)
			.filter(
				(key) =>
					this.conversationStore.nodes[key] &&
					this.conversationStore.nodes[key].type === ConversationNodeType.Path &&
					(this.conversationStore.nodes[key].data as Path).intents.some((i) => i.id === this.intent.id)
			)
			.map((key) => this.conversationStore.nodes[key] as ConversationNode<Path>);

		paths.forEach((path) => {
			let entityValueIds = path.data.entityValueIds;

			// If path contains intent other than current intent that uses the same entity,
			// then add entity values to the same path,
			// else update the entity values from old to new.
			if (
				path.data.intents.some(
					(i) =>
						i.id !== this.intent.id &&
						this.conversationStore.intents.directory[i.id] &&
						this.conversationStore.intents.directory[i.id].entityId &&
						this.conversationStore.intents.directory[i.id].entityId === this.selectedEntity?.id
				)
			) {
				entityValueIds = entityValueIds.concat(
					entityValueIds.map((evId) => {
						if (newOldValueIdMap[evId]) {
							return newOldValueIdMap[evId];
						} else {
							return evId;
						}
					})
				);
			} else {
				entityValueIds = entityValueIds.map((evId) => {
					if (newOldValueIdMap[evId]) {
						return newOldValueIdMap[evId];
					} else {
						return evId;
					}
				});
			}
			this.conversationStore.updateNodeData({
				id: path.id,
				data: {
					...path.data,
					entityValueIds
				}
			});
		});

		if (this.intent) {
			// update current intent for component.
			const updateIntent = {
				...this.intent,
				entityId: newId
			};

			this.$emit("updateIntent", updateIntent);

			// update current intent in store.
			this.conversationStore.updateIntent({ id: this.intent.id, intent: { ...updateIntent } });
		}

		// remove old entity if no reference exists.
		this.conversationStore.removeEntityIfNoReferenceExists(this.selectedEntity.id);

		// add new entity in store.
		this.conversationStore.addEntity(newEntity);
	}

	navigateToCommonIntents() {
		this.$router.push(Paths.CommonIntents);
	}

	navigateToCommonEntities() {
		this.$router.push(Paths.UtteranceEntities);
	}
}

interface IntentFormFields extends Intent {
	entityName: string;
}
